import React, { memo, useId, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import clsx from 'clsx';
import type { WorkOrderSort } from '../../../../../enums/workOrderSort';
import type { WorkOrderStatus } from '../../../../../enums/workOrderStatus';
import type { WorkOrderDragItem, WorkOrderListItem } from '../../../../../types/InfrastructureManagerTypes';
import { WORK_ORDER_DRAG_TYPE } from '../../constants';
import { isLightColor, sortOrders } from '../../workOrderList.utils';
import WorkOrderRow from '../WorkOrderRow';
import ColumnHeader from './ColumnHeader';
import styles from './StatusColumn.module.scss';

interface StatusColumnProps {
  status: WorkOrderStatus;
  orders: WorkOrderListItem[];
  sort: WorkOrderSort;
  color?: string;
  selectedId: number | null;
  canDrag: boolean;
  onSortChange: (status: WorkOrderStatus, sort: WorkOrderSort) => void;
  onSelect: (id: number) => void;
  onExpand: (id: number) => void;
  onDrop: (item: WorkOrderDragItem, to: WorkOrderStatus) => void;
}

const StatusColumn = ({
  status,
  orders,
  sort,
  color,
  selectedId,
  canDrag,
  onSortChange,
  onSelect,
  onExpand,
  onDrop,
}: StatusColumnProps) => {
  const headingId = useId();
  const sortedOrders = useMemo(() => sortOrders(orders, sort), [orders, sort]);

  const [{ isDropTarget }, dropRef] = useDrop(
    () => ({
      accept: WORK_ORDER_DRAG_TYPE,
      canDrop: (item: WorkOrderDragItem) => item.from !== status,
      drop: (item: WorkOrderDragItem) => onDrop(item, status),
      collect: (monitor) => ({ isDropTarget: monitor.isOver() && monitor.canDrop() }),
    }),
    [status, onDrop],
  );

  const columnStyle = { '--wo-col-w': `var(--wo-col-${status})`, '--wo-pill-bg': color } as React.CSSProperties;
  const columnClassName = clsx(styles.statusColumn, {
    [styles.statusColumnLightPill]: color && isLightColor(color),
    [styles.statusColumnDropTarget]: isDropTarget,
  });

  return (
    <section ref={dropRef} className={columnClassName} style={columnStyle} aria-labelledby={headingId}>
      <ColumnHeader
        status={status}
        headingId={headingId}
        count={orders.length}
        sort={sort}
        onSortChange={onSortChange}
      />
      <div className={styles.statusColumnBody}>
        <ul className={styles.statusColumnRows}>
          {sortedOrders.length ? (
            sortedOrders.map((order) => (
              <WorkOrderRow
                key={order.id}
                order={order}
                isSelected={order.id === selectedId}
                canDrag={canDrag}
                onSelect={onSelect}
                onExpand={onExpand}
              />
            ))
          ) : (
            <li className={styles.statusColumnEmpty}>No work orders</li>
          )}
        </ul>
      </div>
    </section>
  );
};

export default memo(StatusColumn);
